import branch, { BranchError, SessionData } from 'branch-sdk';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { Header, Loader } from '../../components';
import { AccountContext, AccountContextInterface } from '../../contexts';
import Logger from '../../logger';
import { Account, Cred, GiveReccoPayload } from '../../models';
import { POST_RECCO_STEPS, ScreenState } from '../../types';
import { getDisplayErrorMsg, setCookie } from '../../utils';
import './styles.scss';
import { CredCard } from '../../components/cred-card';
import { PostRecco } from '../../components/give-recco';
import { VerifyUser } from '../../components/give-recco/verify-user';
import { DownloadReccoScreen } from '../../components/download-recco-screen';
import { Strings } from '../../constants';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FileType } from 'rsuite/esm/Uploader';

export const GiveRecco: React.FC = () => {
  const { accountService } = useContext<AccountContextInterface>(AccountContext);
  const [reccoResources, setReccoResources] = useState<FileType[]>([]);
  const [displayError, setDisplayError] = useState<string>('');
  const [reccoText, setReccoText] = useState<string>('');
  const [state, setState] = useState<ScreenState>(ScreenState.LOADING);
  const [step, setStep] = useState<POST_RECCO_STEPS>(POST_RECCO_STEPS.LANDING);
  const [account, setAccount] = useState<Account>();
  const [credData, setCredData] = useState<Cred>();
  const [heading, setHeading] = useState<string>(Strings.RECCO_DETAILS_CLICKED_HEADING);
  const [subHeading, setSubHeading] = useState<string | undefined>(undefined);
  const [message, setMessage] = useState<string>(Strings.RECCO_DETAILS_CLICKED_MESSAGE);

  useEffect(() => {
    branch.data((err: BranchError, data: SessionData | null) => {
      err && Logger.error('Branch Error: ' + err);
      if (data?.data_parsed && (data?.data_parsed as any)?.credId) {
        const parsedData = data.data_parsed as any;
        setState(ScreenState.DEFAULT);
        parsedData.token && setCookie('token', parsedData.token, 1);
        fetchCred(parsedData?.credId);
      }
    });
  }, []);

  const fetchCred = async (credId: string) => {
    const { data: res, error: fetchError } = await accountService.getCred(credId) as any;
    setCredData(res);

    if (fetchError) {
      setHeading(Strings.SHARE_LINK_EXPIRED_HEADING);
      setSubHeading(Strings.SHARE_LINK_EXPIRED_SUB_HEADING);
      setMessage(Strings.SHARE_LINK_EXPIRED_MESSAGE);
      setStep(POST_RECCO_STEPS.DOWNLOAD_SCREEN);
    }
  };

  const handleCredDetailsClicked = () => {
    setHeading(Strings.RECCO_DETAILS_CLICKED_HEADING);
    setSubHeading(undefined);
    setMessage(Strings.RECCO_DETAILS_CLICKED_MESSAGE);
    setStep(POST_RECCO_STEPS.DOWNLOAD_SCREEN);
  };

  const handleReccoPosted = () => {
    setHeading(Strings.POST_RECCO_SUBMIT_HEADING);
    setSubHeading(undefined);
    setMessage(Strings.POST_RECCO_SUBMIT_MESSAGE);
    setStep(POST_RECCO_STEPS.DOWNLOAD_SCREEN);
  };

  const createRecco = async () => {
    const resourceIds: string[] = [];
    const toastId = toast.loading('Creating your Recco...');

    for (let i = 0; i < reccoResources.length; i++) {
      const resource = reccoResources[i];

      if (resource.blobFile) {
        const formData = new FormData();
        formData.append('source', 'RECCO');
        formData.append('file', resource.blobFile, resource.name);

        toast.update(toastId, { render: `Uploading ${resource.name}...`, isLoading: true });

        const { data: uploadData, error: uploadErr } = await accountService.createReccoResource(account!!.id, formData);

        if (uploadData && !uploadErr) {
          resourceIds.push(...uploadData.map(res => res.id));
          toast.update(toastId, { render: `${resource.name} uploaded successfully!`, isLoading: true });
        } else {
          setState(ScreenState.ERROR);
          setDisplayError(getDisplayErrorMsg(uploadErr));
          toast.update(toastId, { render: `Failed to upload ${resource.name}.`, type: 'error', isLoading: false, autoClose: 5000 });
          return;
        }
      }
    }

    toast.update(toastId, { render: 'Finalizing Recco...', isLoading: true });

    const payload: GiveReccoPayload = {
      message: reccoText,
      resources: resourceIds,
    };

    const { data, error } = await accountService.createRecco(account!!.id, credData!!.id, payload);

    if (data?.id && !error) {
      toast.update(toastId, { render: 'Recco posted successfully!', type: 'success', isLoading: false, autoClose: 5000 });
      handleReccoPosted();
    } else {
      setState(ScreenState.ERROR);
      setDisplayError(getDisplayErrorMsg(error));
      toast.update(toastId, { render: 'Failed to post Recco. Please try again.', type: 'error', isLoading: false, autoClose: 5000 });
    }
  };



  const loading = state === ScreenState.LOADING;

  const handleGiveReccoClick = () => {
    setStep(POST_RECCO_STEPS.POST);
  };

  return (
    <>
      <Header />
      {loading ? (
        <Loader containerCls="pageLoader" />
      ) : (
        <div className="container">
          <div className="row">
            {step === POST_RECCO_STEPS.LANDING && credData && (
              <CredCard
                cred={credData}
                handleGiveReccoClick={handleGiveReccoClick}
                handleCredDetailsClicked={handleCredDetailsClicked}
              />
            )}

            {step === POST_RECCO_STEPS.POST && (
              <PostRecco
                setStep={setStep}
                reccoText={reccoText}
                setReccoText={setReccoText}
                reccoResources={reccoResources}
                setReccoResources={setReccoResources}
              />
            )}

            {step === POST_RECCO_STEPS.VERIFY && (
              <VerifyUser
                state={state}
                setState={setState}
                account={account}
                setAccount={setAccount}
                displayError={displayError}
                setDisplayError={setDisplayError}
                createRecco={createRecco}
              />
            )}
            <ToastContainer />

            {step === POST_RECCO_STEPS.DOWNLOAD_SCREEN && (
              <DownloadReccoScreen
                heading={heading}
                subHeading={subHeading}
                message={message}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
